import { useCallback, useMemo, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaMinus, FaPlus } from 'react-icons/fa';
import { colors } from '../../../../colors';
import { IndicatorsConfig } from '../../../../screens/ProfitCalculations/types';

import './index.css';
import ColoredCheckbox from '../../../ColoredCheckbox';
import InputField from '../../../Form/InputField';

type Indicators = Array<{
  groupTitle: string;
  group: Array<IndicatorItem[]>;
}>;

type IndicatorItem = {
  title: string;
  isEnabled: boolean;
  toggleEnabled: () => void;
  color?: string;
  input: Array<{
    value: number | boolean | null;
    handleValueChange: ((value: string) => void) | ((value: boolean) => void);
    placeholder: string;
    isCheckbox?: boolean;
  }>;
  handleMinusPress?: () => void;
  handlePlusPress?: () => void;
};

interface SectionProps {
  indicators: IndicatorItem[][];
  titel: string;
  availableWidth: number | undefined;
}

const IndicatorLegendSection = (props: SectionProps) => {
  const { indicators, titel, availableWidth } = props;

  /** We have to make the form more responsive
   * Because transaction candles is a significantly smaller area
   * and we want things to look mostly the same
   * We want to apply minimum and maximum sizes though
   *
   * We'll create the sizes based on
   * the available total width of the parent
   */

  const checkboxSize = useMemo(() => {
    if (!availableWidth) {
      return 12;
    }

    return -2 + (availableWidth * 1.6) / 150;
  }, [availableWidth]);

  const labelSize = useMemo(() => {
    if (!availableWidth) {
      return 20;
    }

    return -2 + (availableWidth * 0.8) / 50;
  }, [availableWidth]);

  const plusMinusSize = useMemo(() => {
    if (!availableWidth) {
      return 20;
    }

    return -2 + (availableWidth * 1.5) / 110;
  }, [availableWidth]);

  const fieldMaxSize = useMemo(() => {
    if (!availableWidth) {
      return undefined;
    }

    return availableWidth / 5.5;
  }, [availableWidth]);

  return (
    <div className="indicators-legend-section-container">
      {/** TEXT: Title */}
      <span className="indicators-legend-section-title">{titel}</span>

      {/** FOR EACH indicator */}
      {/** CONTAINER: Item */}
      {indicators.map((group, groupIndex) => (
        <div
          className="indicators-legend-row-container"
          key={`overlay_indicator_group_${groupIndex}`}
        >
          {group.map((indicator) => (
            <div
              className="indicators-legend-item-container"
              key={`overlay_indicator_${indicator.title}`}
            >
              {/** CONTAINER: Top Row */}
              <div className="indicators-legend-row-container">
                {/** ICON: Checkbox */}
                <div className="indicators-legend-checkbox-container">
                  <ColoredCheckbox
                    value={indicator.isEnabled}
                    handleValueChange={indicator.toggleEnabled}
                    size={checkboxSize}
                    color={indicator.color || colors.white}
                  />
                </div>
                {/** TEXT: Indicator name */}
                <span
                  className="indicators-legend-section-text"
                  style={{ fontSize: labelSize }}
                >
                  {indicator.title}
                </span>
              </div>
              {/** CONTAINER: Bottom Row */}
              <div className="indicators-legend-row-container">
                {/** INPUT */}
                <>
                  {indicator.input.map((input, inputIndex) => (
                    <div
                      className="indicators-legend-input-container"
                      key={`input_${indicator.title}_${inputIndex}`}
                    >
                      {input.isCheckbox ? (
                        <div className="indicators-legend-row-container">
                          {/** ICON: Checkbox */}
                          <div className="indicators-legend-checkbox-container">
                            <ColoredCheckbox
                              value={input.value as boolean}
                              handleValueChange={
                                input.handleValueChange as (
                                  value: boolean,
                                ) => void
                              }
                              size={checkboxSize}
                              color={indicator.color || colors.white}
                            />
                          </div>
                          {/** TEXT: Indicator name */}
                          <span className="indicators-legend-section-text">
                            {input.placeholder}
                          </span>
                        </div>
                      ) : (
                        <InputField
                          config={{
                            label: input.placeholder,
                            value: input.value ? input.value.toString() : '',
                            handleValueChange: input.handleValueChange,
                            shouldFit: true,
                            fieldMaxSize,
                          }}
                        />
                      )}
                    </div>
                  ))}
                  {!!indicator.handleMinusPress && (
                    <FaMinus
                      color={colors.white}
                      size={plusMinusSize}
                      onClick={indicator.handleMinusPress}
                      className="indicators-legend-minus"
                    />
                  )}
                  {!!indicator.handlePlusPress && (
                    <FaPlus
                      color={colors.white}
                      size={plusMinusSize}
                      onClick={indicator.handlePlusPress}
                      className="indicators-legend-plus"
                    />
                  )}
                </>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

interface Props {
  indicatorsConfig: IndicatorsConfig;
  handleIndicatorsConfigChange: (
    updatedIndicatorConfig: IndicatorsConfig,
  ) => void;
  availableWidth: number | undefined;
}

const IndicatorsLegend = (props: Props) => {
  const { indicatorsConfig, handleIndicatorsConfigChange, availableWidth } =
    props;

  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleToggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  /** OVERLAYS */

  const handleSMAToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        simple: {
          ...indicatorsConfig.ma.simple,
          isEnabled: !indicatorsConfig.ma.simple.isEnabled,
        },
        waver: {
          ...indicatorsConfig.ma.waver,
          simple: {
            ...indicatorsConfig.ma.waver.simple,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleEMAToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        exponential: {
          ...indicatorsConfig.ma.exponential,
          isEnabled: !indicatorsConfig.ma.exponential.isEnabled,
        },
        waver: {
          ...indicatorsConfig.ma.waver,
          exponential: {
            ...indicatorsConfig.ma.waver.exponential,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleSMAWaverToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        simple: {
          ...indicatorsConfig.ma.simple,
          isEnabled: false,
        },
        waver: {
          ...indicatorsConfig.ma.waver,
          simple: {
            ...indicatorsConfig.ma.waver.simple,
            isEnabled: !indicatorsConfig.ma.waver.simple.isEnabled,
          },
          exponential: {
            ...indicatorsConfig.ma.waver.exponential,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleEMAWaverToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        exponential: {
          ...indicatorsConfig.ma.exponential,
          isEnabled: false,
        },
        waver: {
          ...indicatorsConfig.ma.waver,
          simple: {
            ...indicatorsConfig.ma.waver.simple,
            isEnabled: false,
          },
          exponential: {
            ...indicatorsConfig.ma.waver.exponential,
            isEnabled: !indicatorsConfig.ma.waver.exponential.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleBollingerBandToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      bands: {
        ...indicatorsConfig.bands,
        bb: {
          ...indicatorsConfig.bands.bb,
          overlay: {
            ...indicatorsConfig.bands.bb.overlay,
            isEnabled: !indicatorsConfig.bands.bb.overlay.isEnabled,
          },
        },
        keltner: {
          ...indicatorsConfig.bands.keltner,
          overlay: {
            ...indicatorsConfig.bands.keltner.overlay,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleKeltnerChannelToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      bands: {
        ...indicatorsConfig.bands,
        bb: {
          ...indicatorsConfig.bands.bb,
          overlay: {
            ...indicatorsConfig.bands.bb.overlay,
            isEnabled: false,
          },
        },
        keltner: {
          ...indicatorsConfig.bands.keltner,
          overlay: {
            ...indicatorsConfig.bands.keltner.overlay,
            isEnabled: !indicatorsConfig.bands.keltner.overlay.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  /** INDICATORS */

  const handleRSIToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      rsi: {
        ...indicatorsConfig.rsi,
        standard: {
          ...indicatorsConfig.rsi.standard,
          isEnabled: !indicatorsConfig.rsi.standard.isEnabled,
        },
        waver: {
          ...indicatorsConfig.rsi.waver,
          standard: {
            ...indicatorsConfig.rsi.waver.standard,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleStochasticRSIToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      rsi: {
        ...indicatorsConfig.rsi,
        stochastic: {
          ...indicatorsConfig.rsi.stochastic,
          isEnabled: !indicatorsConfig.rsi.stochastic.isEnabled,
        },
        waver: {
          ...indicatorsConfig.rsi.waver,
          stochastic: {
            ...indicatorsConfig.rsi.waver.stochastic,
            isEnabled: false,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleRSIWaverToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      rsi: {
        ...indicatorsConfig.rsi,
        standard: {
          ...indicatorsConfig.rsi.standard,
          isEnabled: false,
        },
        waver: {
          ...indicatorsConfig.rsi.waver,
          standard: {
            ...indicatorsConfig.rsi.waver.standard,
            isEnabled: !indicatorsConfig.rsi.waver.standard.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleStochasticRSIWaverToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      rsi: {
        ...indicatorsConfig.rsi,
        stochastic: {
          ...indicatorsConfig.rsi.stochastic,
          isEnabled: false,
        },
        waver: {
          ...indicatorsConfig.rsi.waver,
          stochastic: {
            ...indicatorsConfig.rsi.waver.stochastic,
            isEnabled: !indicatorsConfig.rsi.waver.stochastic.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleConnorsRSIToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      rsi: {
        ...indicatorsConfig.rsi,
        connors: {
          ...indicatorsConfig.rsi.connors,
          isEnabled: !indicatorsConfig.rsi.connors.isEnabled,
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleMovingAverageConvergenceDivergenceToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        macd: {
          ...indicatorsConfig.ma.macd,
          isEnabled: !indicatorsConfig.ma.macd.isEnabled,
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleBollingerBandConvergenceDivergenceToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      bands: {
        ...indicatorsConfig.bands,
        bb: {
          ...indicatorsConfig.bands.bb,
          bbcd: {
            ...indicatorsConfig.bands.bb.bbcd,
            isEnabled: !indicatorsConfig.bands.bb.bbcd.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleKeltnerChannelConvergenceDivergenceToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      bands: {
        ...indicatorsConfig.bands,
        keltner: {
          ...indicatorsConfig.bands.keltner,
          kccd: {
            ...indicatorsConfig.bands.keltner.kccd,
            isEnabled: !indicatorsConfig.bands.keltner.kccd.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleMarketCipherWavesToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      marketCipher: {
        ...indicatorsConfig.marketCipher,
        waves: {
          ...indicatorsConfig.marketCipher.waves,
          isEnabled: !indicatorsConfig.marketCipher.waves.isEnabled,
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handlePriceOverExtensionToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        priceOverExtension: {
          ...indicatorsConfig.ma.priceOverExtension,
          isEnabled: !indicatorsConfig.ma.priceOverExtension.isEnabled,
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handlePriceOverExtensionWaverToggle = useCallback(() => {
    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        waver: {
          ...indicatorsConfig.ma.waver,
          priceOverExtension: {
            ...indicatorsConfig.ma.waver.priceOverExtension,
            isEnabled: !indicatorsConfig.ma.waver.priceOverExtension.isEnabled,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  /** OVERLAYS */

  const handleSMAPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          simple: {
            isEnabled: false,
            period: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleEMAPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          exponential: {
            isEnabled: false,
            period: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleSMAWaverPeriodChange = useCallback(
    (value: string, index: number) => {
      const newPeriods = [...indicatorsConfig.ma.waver.simple.periods];
      newPeriods[index] = value ? parseInt(value) : null;

      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            simple: {
              isEnabled: false,
              periods: newPeriods,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleSMAWaverMinusPress = useCallback(() => {
    const newPeriods = [...indicatorsConfig.ma.waver.simple.periods];
    newPeriods.pop();

    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        waver: {
          ...indicatorsConfig.ma.waver,
          simple: {
            isEnabled: false,
            periods: newPeriods,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleSMAWaverPlusPress = useCallback(() => {
    const newPeriods = [...indicatorsConfig.ma.waver.simple.periods];
    newPeriods.push(null);

    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        waver: {
          ...indicatorsConfig.ma.waver,
          simple: {
            isEnabled: false,
            periods: newPeriods,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleEMAWaverPeriodChange = useCallback(
    (value: string, index: number) => {
      const newPeriods = [...indicatorsConfig.ma.waver.exponential.periods];
      newPeriods[index] = value ? parseInt(value) : null;

      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            exponential: {
              isEnabled: false,
              periods: newPeriods,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleEMAWaverMinusPress = useCallback(() => {
    const newPeriods = [...indicatorsConfig.ma.waver.exponential.periods];
    newPeriods.pop();

    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        waver: {
          ...indicatorsConfig.ma.waver,
          exponential: {
            isEnabled: false,
            periods: newPeriods,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleEMAWaverPlusPress = useCallback(() => {
    const newPeriods = [...indicatorsConfig.ma.waver.exponential.periods];
    newPeriods.push(null);

    handleIndicatorsConfigChange({
      ...indicatorsConfig,
      ma: {
        ...indicatorsConfig.ma,
        waver: {
          ...indicatorsConfig.ma.waver,
          exponential: {
            isEnabled: false,
            periods: newPeriods,
          },
        },
      },
    });
  }, [handleIndicatorsConfigChange, indicatorsConfig]);

  const handleBollingerBandPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          bb: {
            ...indicatorsConfig.bands.bb,
            overlay: {
              ...indicatorsConfig.bands.bb.overlay,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleBollingerBandStandardDeviationSizeChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          bb: {
            ...indicatorsConfig.bands.bb,
            overlay: {
              ...indicatorsConfig.bands.bb.overlay,
              isEnabled: false,
              standardDeviationSize: value ? parseFloat(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleBollingerBandZoningDeviationSizeChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          bb: {
            ...indicatorsConfig.bands.bb,
            overlay: {
              ...indicatorsConfig.bands.bb.overlay,
              isEnabled: false,
              zoningDeviationSize: value ? parseFloat(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleBollingerBandShowOverExtensionChange = useCallback(
    (value: boolean) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          bb: {
            ...indicatorsConfig.bands.bb,
            overlay: {
              ...indicatorsConfig.bands.bb.overlay,
              showOverExtension: value,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleKeltnerChannelPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          keltner: {
            ...indicatorsConfig.bands.keltner,
            overlay: {
              ...indicatorsConfig.bands.keltner.overlay,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleKeltnerChannelAverageTrueRangeMultiplierChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          keltner: {
            ...indicatorsConfig.bands.keltner,
            overlay: {
              ...indicatorsConfig.bands.keltner.overlay,
              isEnabled: false,
              averageTrueRangeMultiplier: value ? parseFloat(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleKeltnerChannelZoningTrueRangeMultiplierChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          keltner: {
            ...indicatorsConfig.bands.keltner,
            overlay: {
              ...indicatorsConfig.bands.keltner.overlay,
              isEnabled: false,
              zoningTrueRangeMultiplier: value ? parseFloat(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleKeltnerChannelShowOverExtensionChange = useCallback(
    (value: boolean) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          keltner: {
            ...indicatorsConfig.bands.keltner,
            overlay: {
              ...indicatorsConfig.bands.keltner.overlay,
              showOverExtension: value,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  /** INDICATORS */

  const handleRSIPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          standard: {
            ...indicatorsConfig.rsi.standard,
            isEnabled: false,
            period: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleRSIOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          standard: {
            ...indicatorsConfig.rsi.standard,
            isEnabled: false,
            overExtensionThreshold: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleStochasticRSIPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          stochastic: {
            ...indicatorsConfig.rsi.stochastic,
            isEnabled: false,
            period: value ? parseFloat(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleStochasticRSIOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          stochastic: {
            ...indicatorsConfig.rsi.stochastic,
            isEnabled: false,
            overExtensionThreshold: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleRSIWaverPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          waver: {
            ...indicatorsConfig.rsi.waver,
            standard: {
              ...indicatorsConfig.rsi.standard,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleRSIWaverOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          waver: {
            ...indicatorsConfig.rsi.waver,
            standard: {
              ...indicatorsConfig.rsi.waver.standard,
              isEnabled: false,
              overExtensionThreshold: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleStochasticRSIWaverPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          waver: {
            ...indicatorsConfig.rsi.waver,
            stochastic: {
              ...indicatorsConfig.rsi.waver.stochastic,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleStochasticRSIWaverOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          waver: {
            ...indicatorsConfig.rsi.waver,
            stochastic: {
              ...indicatorsConfig.rsi.waver.stochastic,
              isEnabled: false,
              overExtensionThreshold: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleConnorsRSIPricePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          connors: {
            ...indicatorsConfig.rsi.connors,
            isEnabled: false,
            priceRSIPeriod: value ? parseFloat(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleConnorsRSITrendPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          connors: {
            ...indicatorsConfig.rsi.connors,
            isEnabled: false,
            trendRSIPeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleConnorsRSIRateOfChangePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          connors: {
            ...indicatorsConfig.rsi.connors,
            isEnabled: false,
            rateOfChangePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleConnorsRSIOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        rsi: {
          ...indicatorsConfig.rsi,
          connors: {
            ...indicatorsConfig.rsi.connors,
            isEnabled: false,
            overExtensionThreshold: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleMovingAverageConvergenceDivergenceShortPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          macd: {
            ...indicatorsConfig.ma.macd,
            isEnabled: false,
            shortPeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleMovingAverageConvergenceDivergenceLongPeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          macd: {
            ...indicatorsConfig.ma.macd,
            isEnabled: false,
            longPeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleMovingAverageConvergenceDivergenceSignalPeriodChange =
    useCallback(
      (value: string) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          ma: {
            ...indicatorsConfig.ma,
            macd: {
              ...indicatorsConfig.ma.macd,
              isEnabled: false,
              signalPeriod: value ? parseInt(value) : null,
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleMovingAverageConvergenceDivergenceShowWeightedHistogramChange =
    useCallback(
      (value: boolean) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          ma: {
            ...indicatorsConfig.ma,
            macd: {
              ...indicatorsConfig.ma.macd,
              showWeightedHistogram: value,
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleBollingerBandConvergenceDivergencePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          bb: {
            ...indicatorsConfig.bands.bb,
            bbcd: {
              ...indicatorsConfig.bands.bb.bbcd,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleBollingerBandConvergenceDivergenceStandardDeviationSizeChange =
    useCallback(
      (value: string) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            bb: {
              ...indicatorsConfig.bands.bb,
              bbcd: {
                ...indicatorsConfig.bands.bb.bbcd,
                isEnabled: false,
                standardDeviationSize: value ? parseInt(value) : null,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleBollingerBandConvergenceDivergenceSignalPeriodChange =
    useCallback(
      (value: string) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            bb: {
              ...indicatorsConfig.bands.bb,
              bbcd: {
                ...indicatorsConfig.bands.bb.bbcd,
                isEnabled: false,
                signalPeriod: value ? parseInt(value) : null,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleBollingerBandConvergenceDivergenceShowWeightedHistogramChange =
    useCallback(
      (value: boolean) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            bb: {
              ...indicatorsConfig.bands.bb,
              bbcd: {
                ...indicatorsConfig.bands.bb.bbcd,
                showWeightedHistogram: value,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleKeltnerChannelConvergenceDivergencePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        bands: {
          ...indicatorsConfig.bands,
          keltner: {
            ...indicatorsConfig.bands.keltner,
            kccd: {
              ...indicatorsConfig.bands.keltner.kccd,
              isEnabled: false,
              period: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );
  const handleKeltnerChannelConvergenceDivergenceAverrageTrueRangeSizeChange =
    useCallback(
      (value: string) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            keltner: {
              ...indicatorsConfig.bands.keltner,
              kccd: {
                ...indicatorsConfig.bands.keltner.kccd,
                isEnabled: false,
                averageTrueRangeMultiplier: value ? parseInt(value) : null,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleKeltnerChannelConvergenceDivergenceSignalPeriodChange =
    useCallback(
      (value: string) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            keltner: {
              ...indicatorsConfig.bands.keltner,
              kccd: {
                ...indicatorsConfig.bands.keltner.kccd,
                isEnabled: false,
                signalPeriod: value ? parseInt(value) : null,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleKeltnerChannelConvergenceDivergenceShowWeightedHistogramChange =
    useCallback(
      (value: boolean) => {
        handleIndicatorsConfigChange({
          ...indicatorsConfig,
          bands: {
            ...indicatorsConfig.bands,
            keltner: {
              ...indicatorsConfig.bands.keltner,
              kccd: {
                ...indicatorsConfig.bands.keltner.kccd,
                showWeightedHistogram: value,
              },
            },
          },
        });
      },
      [handleIndicatorsConfigChange, indicatorsConfig],
    );

  const handleMarketCipherWavesPricePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        marketCipher: {
          ...indicatorsConfig.marketCipher,
          waves: {
            ...indicatorsConfig.marketCipher.waves,
            isEnabled: false,
            pricePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleMarketCipherWavesPriceDivergencePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        marketCipher: {
          ...indicatorsConfig.marketCipher,
          waves: {
            ...indicatorsConfig.marketCipher.waves,
            isEnabled: false,
            priceDivergencePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handleMarketCipherWavesWavePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        marketCipher: {
          ...indicatorsConfig.marketCipher,
          waves: {
            ...indicatorsConfig.marketCipher.waves,
            isEnabled: false,
            wavePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionDivergencePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          priceOverExtension: {
            ...indicatorsConfig.ma.priceOverExtension,
            isEnabled: false,
            divergencePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionRangePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          priceOverExtension: {
            ...indicatorsConfig.ma.priceOverExtension,
            isEnabled: false,
            rangePeriod: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          priceOverExtension: {
            ...indicatorsConfig.ma.priceOverExtension,
            isEnabled: false,
            overExtensionThreshold: value ? parseInt(value) : null,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionUseExponantialChange = useCallback(
    (value: boolean) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          priceOverExtension: {
            ...indicatorsConfig.ma.priceOverExtension,
            isEnabled: false,
            useExponantial: value,
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionWaverDivergencePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            priceOverExtension: {
              ...indicatorsConfig.ma.waver.priceOverExtension,
              isEnabled: false,
              divergencePeriod: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionWaverRangePeriodChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            priceOverExtension: {
              ...indicatorsConfig.ma.waver.priceOverExtension,
              isEnabled: false,
              rangePeriod: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionWaverThresholdChange = useCallback(
    (value: string) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            priceOverExtension: {
              ...indicatorsConfig.ma.waver.priceOverExtension,
              isEnabled: false,
              overExtensionThreshold: value ? parseInt(value) : null,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const handlePriceOverExtensionWaverUseExponantialChange = useCallback(
    (value: boolean) => {
      handleIndicatorsConfigChange({
        ...indicatorsConfig,
        ma: {
          ...indicatorsConfig.ma,
          waver: {
            ...indicatorsConfig.ma.waver,
            priceOverExtension: {
              ...indicatorsConfig.ma.waver.priceOverExtension,
              isEnabled: false,
              useExponantial: value,
            },
          },
        },
      });
    },
    [handleIndicatorsConfigChange, indicatorsConfig],
  );

  const indicators: Indicators = useMemo(() => {
    return [
      {
        groupTitle: 'Relative Strength Index:',
        group: [
          [
            {
              title: 'Relative Strength Index',
              isEnabled: indicatorsConfig.rsi.standard.isEnabled,
              toggleEnabled: handleRSIToggle,
              input: [
                {
                  value: indicatorsConfig.rsi.standard.period,
                  handleValueChange: handleRSIPeriodChange,
                  placeholder: 'Period',
                },
                {
                  value: indicatorsConfig.rsi.standard.overExtensionThreshold,
                  handleValueChange: handleRSIOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
              ],
            },
            {
              title: 'Stochastic Relative Strength Index',
              isEnabled: indicatorsConfig.rsi.stochastic.isEnabled,
              toggleEnabled: handleStochasticRSIToggle,
              input: [
                {
                  value: indicatorsConfig.rsi.stochastic.period,
                  handleValueChange: handleStochasticRSIPeriodChange,
                  placeholder: 'Period',
                },
                {
                  value: indicatorsConfig.rsi.stochastic.overExtensionThreshold,
                  handleValueChange:
                    handleStochasticRSIOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
              ],
            },
          ],
          [
            {
              title: 'Relative Strength Index Waver',
              isEnabled: indicatorsConfig.rsi.waver.standard.isEnabled,
              toggleEnabled: handleRSIWaverToggle,
              input: [
                {
                  value: indicatorsConfig.rsi.waver.standard.period,
                  handleValueChange: handleRSIWaverPeriodChange,
                  placeholder: 'Period',
                },
                {
                  value:
                    indicatorsConfig.rsi.waver.standard.overExtensionThreshold,
                  handleValueChange: handleRSIWaverOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
              ],
            },
            {
              title: 'Stochastic Relative Strength Index Waver',
              isEnabled: indicatorsConfig.rsi.waver.stochastic.isEnabled,
              toggleEnabled: handleStochasticRSIWaverToggle,
              input: [
                {
                  value: indicatorsConfig.rsi.waver.stochastic.period,
                  handleValueChange: handleStochasticRSIWaverPeriodChange,
                  placeholder: 'Period',
                },
                {
                  value:
                    indicatorsConfig.rsi.waver.stochastic
                      .overExtensionThreshold,
                  handleValueChange:
                    handleStochasticRSIWaverOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
              ],
            },
          ],
          [
            {
              title: 'Connors Relative Strength Index',
              isEnabled: indicatorsConfig.rsi.connors.isEnabled,
              toggleEnabled: handleConnorsRSIToggle,
              input: [
                {
                  value: indicatorsConfig.rsi.connors.priceRSIPeriod,
                  handleValueChange: handleConnorsRSIPricePeriodChange,
                  placeholder: 'Price RSI Period',
                },
                {
                  value: indicatorsConfig.rsi.connors.trendRSIPeriod,
                  handleValueChange: handleConnorsRSITrendPeriodChange,
                  placeholder: 'Trend RSI Period',
                },
                {
                  value: indicatorsConfig.rsi.connors.rateOfChangePeriod,
                  handleValueChange: handleConnorsRSIRateOfChangePeriodChange,
                  placeholder: 'ROC Period',
                },
                {
                  value: indicatorsConfig.rsi.connors.overExtensionThreshold,
                  handleValueChange:
                    handleConnorsRSIOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
              ],
            },
          ],
        ],
      },
      {
        groupTitle: 'Moving Averages:',
        group: [
          [
            {
              title: 'Simple Moving Average',
              isEnabled: indicatorsConfig.ma.simple.isEnabled,
              toggleEnabled: handleSMAToggle,
              input: [
                {
                  value: indicatorsConfig.ma.simple.period,
                  handleValueChange: handleSMAPeriodChange,
                  placeholder: 'Period',
                },
              ],
            },
            {
              title: 'Exponential Moving Average',
              isEnabled: indicatorsConfig.ma.exponential.isEnabled,
              toggleEnabled: handleEMAToggle,
              input: [
                {
                  value: indicatorsConfig.ma.exponential.period,
                  handleValueChange: handleEMAPeriodChange,
                  placeholder: 'Period',
                },
              ],
            },
          ],
          [
            {
              title: 'Simple Moving Average Waver',
              isEnabled: indicatorsConfig.ma.waver.simple.isEnabled,
              toggleEnabled: handleSMAWaverToggle,
              input: indicatorsConfig.ma.waver.simple.periods.map((p, i) => ({
                value: p,
                handleValueChange: (value: string) =>
                  handleSMAWaverPeriodChange(value, i),
                placeholder: 'Period',
              })),
              handleMinusPress: handleSMAWaverMinusPress,
              handlePlusPress: handleSMAWaverPlusPress,
            },
          ],
          [
            {
              title: 'Exponential Moving Average Waver',
              isEnabled: indicatorsConfig.ma.waver.exponential.isEnabled,
              toggleEnabled: handleEMAWaverToggle,
              input: indicatorsConfig.ma.waver.exponential.periods.map(
                (p, i) => ({
                  value: p,
                  handleValueChange: (value: string) =>
                    handleEMAWaverPeriodChange(value, i),
                  placeholder: 'Period',
                }),
              ),
              handleMinusPress: handleEMAWaverMinusPress,
              handlePlusPress: handleEMAWaverPlusPress,
            },
          ],
          [
            {
              title: 'Moving Average Convergence Divergence',
              isEnabled: indicatorsConfig.ma.macd.isEnabled,
              toggleEnabled: handleMovingAverageConvergenceDivergenceToggle,
              input: [
                {
                  value: indicatorsConfig.ma.macd.shortPeriod,
                  handleValueChange:
                    handleMovingAverageConvergenceDivergenceShortPeriodChange,
                  placeholder: 'Short EMA Period',
                },
                {
                  value: indicatorsConfig.ma.macd.longPeriod,
                  handleValueChange:
                    handleMovingAverageConvergenceDivergenceLongPeriodChange,
                  placeholder: 'Long EMA Period',
                },
                {
                  value: indicatorsConfig.ma.macd.signalPeriod,
                  handleValueChange:
                    handleMovingAverageConvergenceDivergenceSignalPeriodChange,
                  placeholder: 'Signal EMA Period',
                },
                {
                  value: indicatorsConfig.ma.macd.showWeightedHistogram,
                  handleValueChange:
                    handleMovingAverageConvergenceDivergenceShowWeightedHistogramChange,
                  placeholder: 'Show Weighted Histogram',
                  isCheckbox: true,
                },
              ],
            },
          ],
          [
            {
              title: 'Market Cipher Waves',
              isEnabled: indicatorsConfig.marketCipher.waves.isEnabled,
              toggleEnabled: handleMarketCipherWavesToggle,
              input: [
                {
                  value: indicatorsConfig.marketCipher.waves.pricePeriod,
                  handleValueChange: handleMarketCipherWavesPricePeriodChange,
                  placeholder: 'Price EMA Period',
                },
                {
                  value:
                    indicatorsConfig.marketCipher.waves.priceDivergencePeriod,
                  handleValueChange:
                    handleMarketCipherWavesPriceDivergencePeriodChange,
                  placeholder: 'Divergence Period',
                },
                {
                  value: indicatorsConfig.marketCipher.waves.wavePeriod,
                  handleValueChange: handleMarketCipherWavesWavePeriodChange,
                  placeholder: 'Wave SMA Period',
                },
              ],
            },
          ],
          [
            {
              title: 'Price Over Extension',
              isEnabled: indicatorsConfig.ma.priceOverExtension.isEnabled,
              toggleEnabled: handlePriceOverExtensionToggle,
              input: [
                {
                  value:
                    indicatorsConfig.ma.priceOverExtension.divergencePeriod,
                  handleValueChange:
                    handlePriceOverExtensionDivergencePeriodChange,
                  placeholder: 'Price Period',
                },
                {
                  value: indicatorsConfig.ma.priceOverExtension.rangePeriod,
                  handleValueChange: handlePriceOverExtensionRangePeriodChange,
                  placeholder: 'Range Period',
                },
                {
                  value:
                    indicatorsConfig.ma.priceOverExtension
                      .overExtensionThreshold,
                  handleValueChange: handlePriceOverExtensionThresholdChange,
                  placeholder: 'Over Extension T.',
                },
                {
                  value: indicatorsConfig.ma.priceOverExtension.useExponantial,
                  handleValueChange:
                    handlePriceOverExtensionUseExponantialChange,
                  placeholder: 'Use Exponantial Moving Avergae',
                  isCheckbox: true,
                },
              ],
            },
          ],
          [
            {
              title: 'Price Over Extension Waver',
              isEnabled: indicatorsConfig.ma.waver.priceOverExtension.isEnabled,
              toggleEnabled: handlePriceOverExtensionWaverToggle,
              input: [
                {
                  value:
                    indicatorsConfig.ma.waver.priceOverExtension
                      .divergencePeriod,
                  handleValueChange:
                    handlePriceOverExtensionWaverDivergencePeriodChange,
                  placeholder: 'Price Period',
                },
                {
                  value:
                    indicatorsConfig.ma.waver.priceOverExtension.rangePeriod,
                  handleValueChange:
                    handlePriceOverExtensionWaverRangePeriodChange,
                  placeholder: 'Range Period',
                },
                {
                  value:
                    indicatorsConfig.ma.waver.priceOverExtension
                      .overExtensionThreshold,
                  handleValueChange:
                    handlePriceOverExtensionWaverThresholdChange,
                  placeholder: 'Over Extension T.',
                },
                {
                  value:
                    indicatorsConfig.ma.waver.priceOverExtension.useExponantial,
                  handleValueChange:
                    handlePriceOverExtensionWaverUseExponantialChange,
                  placeholder: 'Use Exponantial Moving Avergae',
                  isCheckbox: true,
                },
              ],
            },
          ],
        ],
      },
      {
        groupTitle: 'Channels:',
        group: [
          [
            {
              title: 'Bollinger Band',
              isEnabled: indicatorsConfig.bands.bb.overlay.isEnabled,
              toggleEnabled: handleBollingerBandToggle,
              input: [
                {
                  value: indicatorsConfig.bands.bb.overlay.period,
                  handleValueChange: (value: string) =>
                    handleBollingerBandPeriodChange(value),
                  placeholder: 'Period',
                },
                {
                  value:
                    indicatorsConfig.bands.bb.overlay.standardDeviationSize,
                  handleValueChange: (value: string) =>
                    handleBollingerBandStandardDeviationSizeChange(value),
                  placeholder: 'Standard Deviation',
                },
                {
                  value: indicatorsConfig.bands.bb.overlay.zoningDeviationSize,
                  handleValueChange: (value: string) =>
                    handleBollingerBandZoningDeviationSizeChange(value),
                  placeholder: 'Zoning Deviation',
                },
                {
                  value: indicatorsConfig.bands.bb.overlay.showOverExtension,
                  handleValueChange: handleBollingerBandShowOverExtensionChange,
                  placeholder: 'Show Price Over Extension',
                  isCheckbox: true,
                },
              ],
            },
          ],
          [
            {
              title: 'Bollinger Band Convergence Divergence',
              isEnabled: indicatorsConfig.bands.bb.bbcd.isEnabled,
              toggleEnabled: handleBollingerBandConvergenceDivergenceToggle,
              input: [
                {
                  value: indicatorsConfig.bands.bb.bbcd.period,
                  handleValueChange:
                    handleBollingerBandConvergenceDivergencePeriodChange,
                  placeholder: 'Period',
                },
                {
                  value: indicatorsConfig.bands.bb.bbcd.standardDeviationSize,
                  handleValueChange:
                    handleBollingerBandConvergenceDivergenceStandardDeviationSizeChange,
                  placeholder: 'Standard Deviation',
                },
                {
                  value: indicatorsConfig.bands.bb.bbcd.signalPeriod,
                  handleValueChange:
                    handleBollingerBandConvergenceDivergenceSignalPeriodChange,
                  placeholder: 'Signal EMA Period',
                },
                {
                  value: indicatorsConfig.bands.bb.bbcd.showWeightedHistogram,
                  handleValueChange:
                    handleBollingerBandConvergenceDivergenceShowWeightedHistogramChange,
                  placeholder: 'Show Weighted Histogram',
                  isCheckbox: true,
                },
              ],
            },
          ],
          [
            {
              title: 'Keltner Channel',
              isEnabled: indicatorsConfig.bands.keltner.overlay.isEnabled,
              toggleEnabled: handleKeltnerChannelToggle,
              input: [
                {
                  value: indicatorsConfig.bands.keltner.overlay.period,
                  handleValueChange: (value: string) =>
                    handleKeltnerChannelPeriodChange(value),
                  placeholder: 'Period',
                },
                {
                  value:
                    indicatorsConfig.bands.keltner.overlay
                      .averageTrueRangeMultiplier,
                  handleValueChange: (value: string) =>
                    handleKeltnerChannelAverageTrueRangeMultiplierChange(value),
                  placeholder: 'Average True Range',
                },
                {
                  value:
                    indicatorsConfig.bands.keltner.overlay
                      .zoningTrueRangeMultiplier,
                  handleValueChange: (value: string) =>
                    handleKeltnerChannelZoningTrueRangeMultiplierChange(value),
                  placeholder: 'Zoning True Range',
                },
                {
                  value:
                    indicatorsConfig.bands.keltner.overlay.showOverExtension,
                  handleValueChange:
                    handleKeltnerChannelShowOverExtensionChange,
                  placeholder: 'Show Price Over Extension',
                  isCheckbox: true,
                },
              ],
            },
          ],
          [
            {
              title: 'Keltner Channel Convergence Divergence',
              isEnabled: indicatorsConfig.bands.keltner.kccd.isEnabled,
              toggleEnabled: handleKeltnerChannelConvergenceDivergenceToggle,
              input: [
                {
                  value: indicatorsConfig.bands.keltner.kccd.period,
                  handleValueChange:
                    handleKeltnerChannelConvergenceDivergencePeriodChange,
                  placeholder: 'Period',
                },
                {
                  value:
                    indicatorsConfig.bands.keltner.kccd
                      .averageTrueRangeMultiplier,
                  handleValueChange:
                    handleKeltnerChannelConvergenceDivergenceAverrageTrueRangeSizeChange,
                  placeholder: 'Average True Range',
                },
                {
                  value: indicatorsConfig.bands.keltner.kccd.signalPeriod,
                  handleValueChange:
                    handleKeltnerChannelConvergenceDivergenceSignalPeriodChange,
                  placeholder: 'Signal EMA Period',
                },
                {
                  value:
                    indicatorsConfig.bands.keltner.kccd.showWeightedHistogram,
                  handleValueChange:
                    handleKeltnerChannelConvergenceDivergenceShowWeightedHistogramChange,
                  placeholder: 'Show Weighted Histogram',
                  isCheckbox: true,
                },
              ],
            },
          ],
        ],
      },
    ];
  }, [
    handleBollingerBandConvergenceDivergencePeriodChange,
    handleBollingerBandConvergenceDivergenceShowWeightedHistogramChange,
    handleBollingerBandConvergenceDivergenceSignalPeriodChange,
    handleBollingerBandConvergenceDivergenceStandardDeviationSizeChange,
    handleBollingerBandConvergenceDivergenceToggle,
    handleBollingerBandPeriodChange,
    handleBollingerBandShowOverExtensionChange,
    handleBollingerBandStandardDeviationSizeChange,
    handleBollingerBandToggle,
    handleBollingerBandZoningDeviationSizeChange,
    handleConnorsRSIOverExtensionThresholdChange,
    handleConnorsRSIPricePeriodChange,
    handleConnorsRSIRateOfChangePeriodChange,
    handleConnorsRSIToggle,
    handleConnorsRSITrendPeriodChange,
    handleEMAPeriodChange,
    handleEMAToggle,
    handleEMAWaverMinusPress,
    handleEMAWaverPeriodChange,
    handleEMAWaverPlusPress,
    handleEMAWaverToggle,
    handleKeltnerChannelAverageTrueRangeMultiplierChange,
    handleKeltnerChannelConvergenceDivergenceAverrageTrueRangeSizeChange,
    handleKeltnerChannelConvergenceDivergencePeriodChange,
    handleKeltnerChannelConvergenceDivergenceShowWeightedHistogramChange,
    handleKeltnerChannelConvergenceDivergenceSignalPeriodChange,
    handleKeltnerChannelConvergenceDivergenceToggle,
    handleKeltnerChannelPeriodChange,
    handleKeltnerChannelShowOverExtensionChange,
    handleKeltnerChannelToggle,
    handleKeltnerChannelZoningTrueRangeMultiplierChange,
    handleMarketCipherWavesPriceDivergencePeriodChange,
    handleMarketCipherWavesPricePeriodChange,
    handleMarketCipherWavesToggle,
    handleMarketCipherWavesWavePeriodChange,
    handleMovingAverageConvergenceDivergenceLongPeriodChange,
    handleMovingAverageConvergenceDivergenceShortPeriodChange,
    handleMovingAverageConvergenceDivergenceShowWeightedHistogramChange,
    handleMovingAverageConvergenceDivergenceSignalPeriodChange,
    handleMovingAverageConvergenceDivergenceToggle,
    handlePriceOverExtensionDivergencePeriodChange,
    handlePriceOverExtensionRangePeriodChange,
    handlePriceOverExtensionThresholdChange,
    handlePriceOverExtensionToggle,
    handlePriceOverExtensionUseExponantialChange,
    handlePriceOverExtensionWaverDivergencePeriodChange,
    handlePriceOverExtensionWaverRangePeriodChange,
    handlePriceOverExtensionWaverThresholdChange,
    handlePriceOverExtensionWaverToggle,
    handlePriceOverExtensionWaverUseExponantialChange,
    handleRSIOverExtensionThresholdChange,
    handleRSIPeriodChange,
    handleRSIToggle,
    handleRSIWaverOverExtensionThresholdChange,
    handleRSIWaverPeriodChange,
    handleRSIWaverToggle,
    handleSMAPeriodChange,
    handleSMAToggle,
    handleSMAWaverMinusPress,
    handleSMAWaverPeriodChange,
    handleSMAWaverPlusPress,
    handleSMAWaverToggle,
    handleStochasticRSIOverExtensionThresholdChange,
    handleStochasticRSIPeriodChange,
    handleStochasticRSIToggle,
    handleStochasticRSIWaverOverExtensionThresholdChange,
    handleStochasticRSIWaverPeriodChange,
    handleStochasticRSIWaverToggle,
    indicatorsConfig.bands.bb.bbcd.isEnabled,
    indicatorsConfig.bands.bb.bbcd.period,
    indicatorsConfig.bands.bb.bbcd.showWeightedHistogram,
    indicatorsConfig.bands.bb.bbcd.signalPeriod,
    indicatorsConfig.bands.bb.bbcd.standardDeviationSize,
    indicatorsConfig.bands.bb.overlay.isEnabled,
    indicatorsConfig.bands.bb.overlay.period,
    indicatorsConfig.bands.bb.overlay.showOverExtension,
    indicatorsConfig.bands.bb.overlay.standardDeviationSize,
    indicatorsConfig.bands.bb.overlay.zoningDeviationSize,
    indicatorsConfig.bands.keltner.kccd.averageTrueRangeMultiplier,
    indicatorsConfig.bands.keltner.kccd.isEnabled,
    indicatorsConfig.bands.keltner.kccd.period,
    indicatorsConfig.bands.keltner.kccd.showWeightedHistogram,
    indicatorsConfig.bands.keltner.kccd.signalPeriod,
    indicatorsConfig.bands.keltner.overlay.averageTrueRangeMultiplier,
    indicatorsConfig.bands.keltner.overlay.isEnabled,
    indicatorsConfig.bands.keltner.overlay.period,
    indicatorsConfig.bands.keltner.overlay.showOverExtension,
    indicatorsConfig.bands.keltner.overlay.zoningTrueRangeMultiplier,
    indicatorsConfig.ma.exponential.isEnabled,
    indicatorsConfig.ma.exponential.period,
    indicatorsConfig.ma.macd.isEnabled,
    indicatorsConfig.ma.macd.longPeriod,
    indicatorsConfig.ma.macd.shortPeriod,
    indicatorsConfig.ma.macd.showWeightedHistogram,
    indicatorsConfig.ma.macd.signalPeriod,
    indicatorsConfig.ma.priceOverExtension.divergencePeriod,
    indicatorsConfig.ma.priceOverExtension.isEnabled,
    indicatorsConfig.ma.priceOverExtension.overExtensionThreshold,
    indicatorsConfig.ma.priceOverExtension.rangePeriod,
    indicatorsConfig.ma.priceOverExtension.useExponantial,
    indicatorsConfig.ma.simple.isEnabled,
    indicatorsConfig.ma.simple.period,
    indicatorsConfig.ma.waver.exponential.isEnabled,
    indicatorsConfig.ma.waver.exponential.periods,
    indicatorsConfig.ma.waver.priceOverExtension.divergencePeriod,
    indicatorsConfig.ma.waver.priceOverExtension.isEnabled,
    indicatorsConfig.ma.waver.priceOverExtension.overExtensionThreshold,
    indicatorsConfig.ma.waver.priceOverExtension.rangePeriod,
    indicatorsConfig.ma.waver.priceOverExtension.useExponantial,
    indicatorsConfig.ma.waver.simple.isEnabled,
    indicatorsConfig.ma.waver.simple.periods,
    indicatorsConfig.marketCipher.waves.isEnabled,
    indicatorsConfig.marketCipher.waves.priceDivergencePeriod,
    indicatorsConfig.marketCipher.waves.pricePeriod,
    indicatorsConfig.marketCipher.waves.wavePeriod,
    indicatorsConfig.rsi.connors.isEnabled,
    indicatorsConfig.rsi.connors.overExtensionThreshold,
    indicatorsConfig.rsi.connors.priceRSIPeriod,
    indicatorsConfig.rsi.connors.rateOfChangePeriod,
    indicatorsConfig.rsi.connors.trendRSIPeriod,
    indicatorsConfig.rsi.standard.isEnabled,
    indicatorsConfig.rsi.standard.overExtensionThreshold,
    indicatorsConfig.rsi.standard.period,
    indicatorsConfig.rsi.stochastic.isEnabled,
    indicatorsConfig.rsi.stochastic.overExtensionThreshold,
    indicatorsConfig.rsi.stochastic.period,
    indicatorsConfig.rsi.waver.standard.isEnabled,
    indicatorsConfig.rsi.waver.standard.overExtensionThreshold,
    indicatorsConfig.rsi.waver.standard.period,
    indicatorsConfig.rsi.waver.stochastic.isEnabled,
    indicatorsConfig.rsi.waver.stochastic.overExtensionThreshold,
    indicatorsConfig.rsi.waver.stochastic.period,
  ]);

  return (
    <div className="indicators-legend-root">
      {/** CONTAINER: Title / collaps clickable */}
      <div
        className="indicators-legend-title-container"
        onClick={handleToggleCollapsed}
      >
        {/** TEXT: Title */}
        <span className="indicators-legend-title-text">Indicators:</span>
        {/** ICON: Chevron */}
        <div className="indicators-legend-chevron-container">
          {isCollapsed ? (
            <FaChevronDown color={colors.white} size={24} />
          ) : (
            <FaChevronUp color={colors.white} size={24} />
          )}
        </div>
      </div>

      {/** CONTAINER: Form / collapsable */}
      {!isCollapsed && (
        <div className="indicators-legend-form-container">
          {indicators.map((indicator) => (
            <IndicatorLegendSection
              key={indicator.groupTitle}
              titel={indicator.groupTitle}
              indicators={indicator.group}
              availableWidth={availableWidth}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default IndicatorsLegend;
